/**
 * M4: the game HUD inventory grid layout (4×4 baseline). Pure — no DOM,
 * unit-tested. The grid is a 4-column layout with a 4×4 = 16-cell baseline
 * (empty cells stay, RS-style); if configuration permits more items, rows
 * append and the client fits every row into the panel — items are never hidden.
 */
import type { InventoryEntry } from "./hud.js";

export interface GridCell {
  entry: InventoryEntry;
  row: number;
  col: number;
  /** Index into the entries array. */
  index: number;
}

export interface GridLayout {
  cols: number;
  rows: number;
  /** Row-major cells; null = empty slot. */
  cells: (GridCell | null)[];
}

export interface GridOptions {
  cols?: number;
  minRows?: number;
}

export const DEFAULT_COLS = 4;
/** The 4×4 footprint: 16 cells, empties stay (16 is enough for this game). */
export const MIN_ROWS = 4;

/** Lay the entries out into a row-major grid. Entries keep their order
 *  (row-major, then column). Empty cells pad up to minRows. */
export function gridCells(entries: readonly InventoryEntry[], opts?: GridOptions): GridLayout {
  const cols = opts?.cols ?? DEFAULT_COLS;
  const minRows = opts?.minRows ?? MIN_ROWS;
  const rows = Math.max(minRows, Math.ceil(entries.length / cols));
  const cells: (GridCell | null)[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      cells.push(i < entries.length ? { entry: entries[i]!, row: r, col: c, index: i } : null);
    }
  }
  return { cols, rows, cells };
}

/** True when the entries need more rows than the baseline 4×4 footprint. */
export function overflowsBaseline(entries: readonly InventoryEntry[], opts?: GridOptions): boolean {
  const cols = opts?.cols ?? DEFAULT_COLS;
  const minRows = opts?.minRows ?? MIN_ROWS;
  return Math.ceil(entries.length / cols) > minRows;
}

/** New entry order after dragging the entry at `from` to slot `to`
 *  (remove-then-insert semantics — the dragged item lands AT the target
 *  slot, shifting everything after it by one). Out-of-range targets clamp
 *  to the ends. Returns the same array reference when nothing moves. */
export function reorderEntries(
  entries: readonly InventoryEntry[],
  from: number,
  to: number,
): InventoryEntry[] {
  if (from < 0 || from >= entries.length || from === to) return [...entries];
  const next = [...entries];
  const [moved] = next.splice(from, 1);
  const clamped = Math.max(0, Math.min(to, next.length));
  next.splice(clamped, 0, moved!);
  return next;
}
